import { useEffect, useState } from "react";

import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue
} from "@chakra-ui/react";
import { FaEllipsisV } from "react-icons/fa";

import { SupplementHistoryEntry } from "~/api/members";
import { useAppStore } from "~/store";
import { formatDate } from "~/utils/date";

// Helper function to sort supplements by ceased date (most recent first)
const sortSupplements = (supplements: SupplementHistoryEntry[]): SupplementHistoryEntry[] => {
  return [...supplements].sort((a, b) => {
    // Sort by ceased date (most recent first)
    const dateA = new Date(a.ceasedDate || "").getTime();
    const dateB = new Date(b.ceasedDate || "").getTime();
    return dateB - dateA;
  });
};

interface RecentSupplementsProps {
  supplements: SupplementHistoryEntry[] | null | undefined;
}

const RecentSupplements = ({ supplements = [] }: RecentSupplementsProps) => {
  const currentMember = useAppStore((state) => state.currentMember);
  const [ceasedSupplements, setCeasedSupplements] = useState<SupplementHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Color mode values
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("blue.100", "blue.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const headerBg = useColorModeValue("blue.50", "blue.900");
  const emptyTextColor = useColorModeValue("gray.500", "gray.400");

  // Update ceased supplements when the supplements prop changes
  useEffect(() => {
    // Sort supplements by ceased date before setting state
    const safeSupplements = Array.isArray(supplements) ? supplements : [];
    const sortedCeasedSupplements = sortSupplements(safeSupplements);
    setCeasedSupplements(sortedCeasedSupplements);
  }, [supplements]);

  // Add console log when component renders
  console.log("RecentSupplements rendering for member:", currentMember?.id);

  if (!currentMember) {
    console.log("No current member, returning null");
    return null;
  }

  // Handle the "Restore" option
  const handleRestoreSupplement = (supplement: SupplementHistoryEntry) => {
    console.log("Restore supplement:", supplement.dosage.supplement);
    // Functionality will be added later
  };

  // Helper to format dosage with frequency
  const formatDosage = (amount?: number | null, unit?: string | null, frequency?: string | null) => {
    if (!amount && !unit && !frequency) return "—";

    let dosage = "";
    if (amount) dosage += amount;
    if (unit) dosage += dosage ? ` ${unit}` : unit;

    // Add frequency if available
    if (frequency) {
      // Convert frequency value to a more readable format with no spaces around slash
      const frequencyMap: Record<string, string> = {
        daily: "/day",
        twice_daily: "/day (twice)",
        every_other_day: "/every other day",
        weekly: "/week",
        monthly: "/month",
        as_needed: "/as needed"
      };

      // Ensure we remove any spaces before the slash
      const formattedFrequency = frequencyMap[frequency] || `/${frequency}`;

      // Make sure there's no space after the unit before adding the slash
      dosage += formattedFrequency;
    }

    return dosage;
  };

  const renderSupplementsTable = (supplements: SupplementHistoryEntry[]) => {
    return (
      <Table size="sm" variant="simple">
        <Thead bg={useColorModeValue("gray.50", "gray.700")}>
          <Tr>
            <Th>Supplement</Th>
            <Th>Dosage</Th>
            <Th>Start Date</Th>
            <Th>Cease Date</Th>
            <Th textAlign="right">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {supplements.map((supplement) => (
            <Tr key={supplement.id}>
              <Td fontWeight="medium">{supplement.dosage.supplement}</Td>
              <Td>{formatDosage(supplement.dosage.amount, supplement.dosage.unit, supplement.dosage.frequency)}</Td>
              <Td>{formatDate(supplement.startDate)}</Td>
              <Td>{formatDate(supplement.ceasedDate || "")}</Td>
              <Td textAlign="right">
                <Menu placement="bottom-end" isLazy>
                  <MenuButton
                    as={IconButton}
                    icon={<FaEllipsisV />}
                    variant="ghost"
                    size="sm"
                    aria-label="Options"
                    _hover={{ bg: useColorModeValue("gray.100", "gray.700") }}
                  />
                  <MenuList fontSize="sm" py={1} boxShadow="md" borderColor={useColorModeValue("gray.200", "gray.700")}>
                    <MenuItem
                      _hover={{ bg: useColorModeValue("gray.100", "gray.700") }}
                      onClick={() => handleRestoreSupplement(supplement)}
                    >
                      Resume taking this
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <Card
      bg={cardBg}
      borderColor={borderColor}
      borderWidth="2px"
      borderRadius="lg"
      boxShadow="lg"
      height="100%"
      minH="300px"
      position="relative"
      overflow="visible"
      mt={6}
    >
      <CardHeader bg={headerBg} py={4} px={5} borderBottom="1px" borderColor={borderColor}>
        <Flex justify="space-between" align="center">
          <Flex align="center">
            <Heading size="md" color={textColor}>
              Recent Supplements
            </Heading>
          </Flex>
          <Text fontSize="xs" color="gray.500">
            Member ID: {currentMember.id}
          </Text>
        </Flex>
      </CardHeader>

      <CardBody>
        {isLoading ? (
          <Box>
            <Skeleton height="30px" width="100%" mb={4} />
            <Skeleton height="30px" width="100%" mb={4} />
            <Skeleton height="30px" width="100%" mb={4} />
          </Box>
        ) : error ? (
          <Text color="red.500" fontSize="sm">
            {error}
          </Text>
        ) : ceasedSupplements.length > 0 ? (
          renderSupplementsTable(ceasedSupplements)
        ) : (
          <Flex direction="column" align="center" justify="center" py={10} textAlign="center">
            <Text fontSize="md" color={emptyTextColor} mb={3}>
              No previously taken supplements
            </Text>
            <Text fontSize="sm" color={emptyTextColor}>
              When this member stops taking a supplement, it will appear here.
            </Text>
          </Flex>
        )}
      </CardBody>
    </Card>
  );
};

export default RecentSupplements;
